/* global BigInt */
import { configureStore, combineReducers } from '@reduxjs/toolkit';
import { selectionDebugMiddleware } from '../middleware/selectionDebugMiddleware';
// ⭐ The one action that empties the store — see slices/sessionActions.js for the leak it closes.
import { SESSION_ENDED } from '../slices/sessionActions';
import { registerSessionEnder } from './sessionBridge';
import userReducer from '../slices/userSlice';
import voiceSnippetReducer from '../slices/voiceSnippetSlice';
import textSnippetReducer from '../slices/textSnippetSlice';
import storyEditorSlice from '../slices/storyEditorSlice';
import userAssetsSlice from '../slices/userAssetsSlice';
import aaiSnipSeqSliceReducer from '../slices/aaiSnipSeqSlice';
import textVoicePairsSliceReducer from '../slices/textVoicePairsSlice';
import charSamplesSliceReducer from '../slices/charSamplesSlice';
import userLitGroupAssetsSlice from '../slices/userLitGroupAssetsSlice';
import aaiCharSequencerSlice from '../slices/aaiCharSequencerSlice';
import cartReducer from '../slices/cartSlice';
import voiceActorReducer from '../slices/voiceActorSlice';
import newBookCreatorReducer from '../slices/newBookCreatorSlice';
import newBookCreatorNormalizedReducer from '../slices/newBookCreatorSliceNormalized';
import userRoleReducer from '../slices/userRoleSlice';
// Opportunity system - Clean architecture: filters and data separate
import opportunityReducer from '../slices/opportunityDataSlice';
import opportunityFiltersReducer from '../slices/opportunityFiltersSlice';
import applicationReducer from '../slices/applicationSlice';
import pathRegistryReducer from '../slices/pathRegistrySlice';
import deleteReducer from '../slices/deleteSlice';
import messageReducer from '../slices/messageSlice';
import securityRegistryReducer from '../slices/securityRegistrySlice';
import statisticsReducer from '../slices/statisticsSlice';
// Voice Sample Management
import sampleCharacterReducer from '../slices/sampleCharacterSlice';

import coreAssetSlice from '../slices/coreAssetsSlice';
import voiceSampleRecReducer from '../slices/voiceSampleRecSlice';
import sseReducer from '../slices/sseSlice';
import recordingReducer from '../slices/aaiRecordingUtilitySlice';

// 🚀 NEW: Clean zone-based bookWorksheet data (ONLY slice now)
import bookWorksheetDataReducer from '../bookWorksheet/slices/bookWorksheetDataSlice';
// 🚀 NEW: BookWorksheet utilities for file uploads and media processing
import bwdUtilitiesReducer from '../bookWorksheet/slices/bwdUtilitiesSlice';
// 🚀 NEW: Clean zone-based voiceActorWorksheet data
import voiceActorDataReducer from '../voiceActorWorksheet/slices/voiceActorDataSlice';
// 🚀 NEW: VoiceActor utilities for file uploads and media processing
import vaUtilitiesReducer from '../voiceActorWorksheet/slices/vaUtilitiesSlice';
// 🚀 Genre options — shared slice for Prototype wizard's genre dropdown.
//   Extracted from the (now-deleted) LitGroup Automation Worksheet slice in F3A_REMOVAL_PLAN.md.
import genreOptionsReducer from '../slices/genreOptionsSlice';
// 🚀 NEW: LitGroup Prototype Builder state
import litGroupPrototypeReducer from '../slices/litGroupPrototypeSlice';
import litGroupCastReducer from '../slices/litGroupCastSlice';
// 🚀 Prototype → LitGroup Publishing
import prototypePublishReducer from '../slices/prototypePublishSlice';
// 🚀 Voice Cloning pipeline (Book Creator)
import voiceCloningReducer from '../slices/voiceCloningSlice';
// 🚀 Voice-readiness stats (Phase 4a — BOOK_VOICE_STATS_SPEC)
import voiceStatsReducer from '../slices/voiceStatsSlice';
// 🚀 Voice rendering (Phase 4c — dry-resolve panel)
import voiceRenderingReducer from '../slices/voiceRenderingSlice';
// 🚀 Voice fidelity (Phase 4d — VA Fidelity Gate, P2 client)
// Keyed by sampleUuid; backs the inline FidelityPanel in RecordingStudio.
import voiceFidelityReducer from '../slices/voiceFidelitySlice';
// 🚀 Admin System Management / database-audit tooling (health, stats, orphans,
//   S3 reconcile/reclaim, reset preview, deletion audit).
import databaseAuditReducer from '../slices/databaseAuditSlice';
import emotionCategoryRegistryReducer from '../slices/emotionCategoryRegistrySlice';


// Remove old slice imports completely to prevent any accidental usage
// These slices have been replaced by bookWorksheetDataSlice
// import bookWorksheetReducer from '../bookWorksheet';
// import bookWorksheetUpgradeReducer from '../bookWorksheet/slices/bookWorksheetSlice_upgrade';
// import bwVariationVersionReducer from '../bookWorksheet/slices/bwVariationVersionSlice';


// Custom serializer for BigInt support
const bigIntSerializer = {
  serialize: {
    replacer: (key, value) => {
      if (typeof value === 'bigint') {
        return {
          __type: 'BigInt',
          value: value.toString()
        };
      }
      return value;
    },
    reviver: (key, value) => {
      if (value && typeof value === 'object' && value.__type === 'BigInt') {
        return BigInt(value.value);
      }
      return value;
    }
  }
};

const userCombinedReducer = combineReducers({
  profile: userReducer,
  voiceSnippets: voiceSnippetReducer,
  storyText: storyEditorSlice,
  textSnippets: textSnippetReducer,
  textVoicePairs: textVoicePairsSliceReducer,
  charSamples: charSamplesSliceReducer,
  userAssets: userAssetsSlice,
  userLitGroupAssets: userLitGroupAssetsSlice,
  aaiCharSequencer: aaiCharSequencerSlice,
  aaiSnipSeq: aaiSnipSeqSliceReducer,
  voiceSampleRec: voiceSampleRecReducer,
  sse: sseReducer,
  recordingUtils: recordingReducer,
  cart: cartReducer,
  voiceActor: voiceActorReducer,
  newBookCreator: newBookCreatorReducer,
  newBookCreatorNormalized: newBookCreatorNormalizedReducer,
  userRole: userRoleReducer,
  // 🚀 CLEAN ARCHITECTURE: Opportunity data (CRUD, storage)
  opportunity: opportunityReducer,
  // 🚀 CLEAN ARCHITECTURE: Opportunity filters (UI, search params, favorites)
  opportunityFilters: opportunityFiltersReducer,
  application: applicationReducer,
  pathRegistry: pathRegistryReducer,
  // 🚀 Universal delete system
  delete: deleteReducer,
  // 🚀 Messaging system
  messages: messageReducer,
  // 🚀 NEW: Clean zone-based bookWorksheet data (ONLY slice now)
  bookWorksheetData: bookWorksheetDataReducer,
  // 🚀 NEW: BookWorksheet utilities for file uploads and media processing
  bwdUtilities: bwdUtilitiesReducer,
  // 🚀 NEW: Clean zone-based voiceActorWorksheet data
  voiceActorWorksheetData: voiceActorDataReducer,
  // 🚀 NEW: VoiceActor utilities for file uploads and media processing
  vaUtilities: vaUtilitiesReducer,
  // map other sub-user reducers here
});

const appReducer = combineReducers({
    user: userCombinedReducer,
    core: coreAssetSlice,
    securityRegistry: securityRegistryReducer,
    statistics: statisticsReducer,
    // Voice Sample Management
    sampleCharacter: sampleCharacterReducer,
    // 🚀 Genre options (shared slice — backs Prototype wizard's genre dropdown)
    genreOptions: genreOptionsReducer,
    // 🚀 LitGroup Prototype Builder
    litGroupPrototype: litGroupPrototypeReducer,
    // 🚀 LitGroup cast merge/alias tooling (dedup imported characters)
    litGroupCast: litGroupCastReducer,
    // 🚀 Prototype → LitGroup Publishing
    prototypePublish: prototypePublishReducer,
    // 🚀 Voice Cloning pipeline (Book Creator)
    voiceCloning: voiceCloningReducer,
    // 🚀 Voice-readiness stats (Phase 4a)
    voiceStats: voiceStatsReducer,
    // 🚀 Voice rendering (Phase 4c dry-resolve panel)
    voiceRendering: voiceRenderingReducer,
    // 🚀 Voice fidelity (Phase 4d — VA Fidelity Gate, P2 client). Sample-keyed
    // map of in-flight / completed scoring envelopes.
    voiceFidelity: voiceFidelityReducer,
    // 🚀 Admin System Management / database-audit tooling
    databaseAudit: databaseAuditReducer,
    // 🚀 Admin — emotion→category translation matrix (editable registry)
    emotionCategoryRegistry: emotionCategoryRegistryReducer,
    // other non-user reducers can go here

});

/**
 * ⭐ THE ROOT RESET. Handing `undefined` to the combined reducer makes every slice rebuild from
 * its own initialState — so ending a session empties the store without any slice having to know
 * that sessions exist.
 *
 * ⛔ WHY IT IS HERE AND NOT IN A SLICE (2026-08-31). Redux state is cleared by a full page load,
 * and logging out navigates with the ROUTER — no reload — so the previous user's data carried
 * straight into the next session. Measured: of the 22+ slices in this store, exactly ONE was
 * cleared at logout. A Voice Actor was shown the Book Creator's marketplace search and results.
 * Per-slice cleanup had already been tried once and had already fallen behind; resetting at the
 * root covers every slice added from here on without anyone remembering to.
 *
 * ⚠️ Nothing is exempt. If some state ever legitimately needs to outlive a session it must be
 * re-hydrated deliberately after login — not quietly left in the store, which is what produced
 * this defect.
 */
const rootReducer = (state, action) =>
  appReducer(action.type === SESSION_ENDED ? undefined : state, action);

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Disable SerializableStateInvariantMiddleware in development to improve performance
      serializableCheck: false,
      // You can also use a more targeted approach if needed:
      // serializableCheck: {
      //   ignoredActions: ['some/action/type'],
      //   ignoredPaths: ['some.path.to.ignore'],
      // },
    }).concat(selectionDebugMiddleware),
  // Configure Redux DevTools with BigInt serialization support
  devTools: process.env.NODE_ENV !== 'production' ? {
    serialize: bigIntSerializer.serialize,
    actionSanitizer: (action) => {
      // Convert BigInt in actions to string for DevTools display
      if (action.meta?.arg?.flags && typeof action.meta.arg.flags === 'bigint') {
        return {
          ...action,
          meta: {
            ...action.meta,
            arg: {
              ...action.meta.arg,
              flags: action.meta.arg.flags.toString() + 'n'
            }
          }
        };
      }
      return action;
    },
    stateSanitizer: (state) => state
  } : false,
});
/**
 * ⭐ THE STORE REGISTERS ITSELF, so code that is not a component can end a session.
 *
 * ⛔ THE DIRECTION MATTERS. `apiClient` and `useIsLoggedIn` clear a dead token and must empty the
 * store as well — the bug on 2026-10-24 was exactly that they did the first and not the second,
 * leaving the app knowing WHO you were while holding no credential. They cannot import this file:
 * the store imports the slices and the slices import apiClient, so that is a cycle whose symptom
 * is an undefined reducer at boot. Registering outwards costs nothing and closes no loop.
 *
 * ⓘ SESSION_ENDED is the root reset already written for the 2026-08-31 cross-user leak: it
 * replaces the whole state rather than resetting slices one at a time.
 */
registerSessionEnder((why) => {
  store.dispatch({ type: SESSION_ENDED, meta: { why: why || null } });
});

// export const userSelector = (state) => state.user;

export default store;
